import type { KeyboardEventHandler, MouseEventHandler } from 'react';
import EventCalendarMonth from './EventsWrapper/EventCalendarMonth';
import type { EventCalendarData } from 'cms/resolvers/eventCalendarResolver';

// Treat 'YYYY-MM-DD' as a date-only (no time-zone drift); otherwise fall back.
export const toEpochMs = (d: string | number): number => {
    if (typeof d === 'number') {
        return d;
    }
    const s = String(d);
    const m = (/^(\d{4})-(\d{2})-(\d{2})$/).exec(s);
    if (m) {
        const [, y, mo, day] = m;
        // Local midnight to avoid TZ drift when combined with other local-based helpers
        return new Date(Number(y), Number(mo) - 1, Number(day), 0, 0, 0, 0).getTime();
    }
    const t = Date.parse(s);
    return Number.isNaN(t) ? 0 : t;
};

// Parse date string or number to local Date object (timezone-safe)
export const parseYMDToLocalDate = (ymd: string | number): Date => {
    // Handle number (timestamp) case
    if (typeof ymd === 'number') {
        return new Date(ymd);
    }

    // Handle string case - accepts 'YYYY-MM-DD' and returns a local Date at midnight
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
};

// Helper function to get unique months that have events (excluding past events in current month)
export const getMonthsWithValidEvents = (data: EventCalendarData): { month: string; year: number; monthIndex: number }[] => {
    const monthsWithEvents = new Set<string>();
    const monthsData: { month: string; year: number; monthIndex: number }[] = [];
    const today = new Date();

    data.eventCalendarCards.forEach(event => {
        const eventDate = new Date(event.date);
        const month = eventDate.toLocaleString('default', { month: 'long' });
        const year = eventDate.getFullYear();
        const monthIndex = eventDate.getMonth();
        // const eventDay = eventDate.getDate();

        // Apply same date filtering logic as EventCalendarMonth
        // const isCurrentMonth = monthIndex === today.getMonth() && year === today.getFullYear();
        const isPastToday = eventDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());

        // Skip past events in current month
        if (isPastToday) {
            return;
        }

        const monthKey = `${month}-${year}`;

        if (!monthsWithEvents.has(monthKey)) {
            monthsWithEvents.add(monthKey);
            monthsData.push({ month, year, monthIndex });
        }
    });

    // Sort by year and month
    return monthsData.sort((a, b) => {
        if (a.year !== b.year) {
            return a.year - b.year;
        }
        return a.monthIndex - b.monthIndex;
    });
};

export const generateMonthButtons = (data: EventCalendarData, handleClick: MouseEventHandler<HTMLButtonElement> | undefined, handleKeyDown: KeyboardEventHandler<HTMLButtonElement> | undefined): JSX.Element[] => {
    const buttons: JSX.Element[] = [];
    const monthsWithEvents = getMonthsWithValidEvents(data);

    let currentYear: number | null = null;

    monthsWithEvents.forEach(({ month, year }) => {
        // Add year separator when we enter a new year
        if (currentYear !== null && year !== currentYear) {
            buttons.push(
                <li
                    className="nav-year-separator"
                    key={`separator-${year}`}
                    role="separator"
                    aria-label={`Year ${year}`}
                >
                    {year}
                </li>
            );
        }
        currentYear = year;

        buttons.push(
            <li key={`${month}-${year}`}>
                <button
                    type="button"
                    id={`${month}-${year}_nav`}
                    className="nav-month-selector"
                    data-month={`${month}-${year}`}
                    key={`${month}-${year}`}
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                    aria-label={`Go to events in ${month} ${year}`}
                >
                    {month}
                </button>
            </li>
        );
    });

    return buttons;
};

export const generateEventSections = (data: EventCalendarData): JSX.Element[] => {
    const sections: JSX.Element[] = [];
    const monthsWithEvents = getMonthsWithValidEvents(data);

    monthsWithEvents.forEach(({ month, year, monthIndex }) => {
        const nextMonthDate = new Date(year, monthIndex + 1, 0);
        const daysInMonth = nextMonthDate.getDate();

        sections.push(
            <EventCalendarMonth
                key={`${month}-${year}`}
                year={year}
                month={month}
                daysInMonth={daysInMonth}
                currentDate={new Date(year, monthIndex, 1)}
                data={data}
            />
        );
    });

    return sections;
};
